import { useState, useRef } from 'react';
import {
  useCalendar,
  useCalendarGrid,
  useCalendarCell,
  useButton,
  useLocale,
  useFocusRing,
  usePress,
  mergeProps,
} from 'react-aria';
import { useCalendarState } from 'react-stately';
import {
  createCalendar,
  getWeeksInMonth,
  type DateValue,
  CalendarDate,
  today,
  getLocalTimeZone,
} from '@internationalized/date';
import { Icon } from '@antimatterhq/ui/components/icon';
import { cn } from '@antimatterhq/ui/lib/utils';
import { Button } from '@antimatterhq/ui/components/button';
import { Text } from '@antimatterhq/ui/components/typography';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@antimatterhq/ui/components/popover';

export type DatePickerProps = {
  /**
   * Currently selected date.
   */
  value?: CalendarDate | null;
  /**
   * Called when the date changes or is cleared.
   */
  onChange?: (value: DateValue | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  dateFormat?: string;
  autoClose?: boolean;
  size?: 'small' | 'default' | 'medium';
  isClearable?: boolean;
  minDate?: CalendarDate;
  maxDate?: CalendarDate;
};

const SingleDatePickerCalendarCell = ({
  state,
  date,
  currentMonth,
}: {
  state: ReturnType<typeof useCalendarState>;
  date: CalendarDate;
  currentMonth: CalendarDate;
}) => {
  const ref = useRef<HTMLButtonElement>(null);

  const { cellProps, buttonProps, isSelected, isDisabled, formattedDate } =
    useCalendarCell({ date }, state, ref);

  const { focusProps, isFocusVisible } = useFocusRing();
  const { pressProps, isPressed } = usePress(buttonProps);

  const isOutsideMonth =
    date.month !== currentMonth.month || date.year !== currentMonth.year;

  // Check if this is today's date
  const todayDate = today(getLocalTimeZone());
  const isToday = date.compare(todayDate) === 0;

  return (
    <td {...cellProps} className="h-9 w-9 p-0">
      <button
        {...mergeProps(buttonProps, pressProps, focusProps)}
        ref={ref}
        className={cn(
          'hover relative flex h-9 w-9 cursor-pointer items-center justify-center rounded text-sm',
          isOutsideMonth && 'invisible',
          !isOutsideMonth && !isDisabled && 'text-gray-900 dark:text-gray-100',
          isDisabled && 'cursor-not-allowed opacity-50',
          !isSelected &&
            !isDisabled &&
            !isOutsideMonth &&
            'hover:bg-gray-100 dark:hover:bg-gray-800',
          isSelected &&
            !isOutsideMonth &&
            'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-700',
          isFocusVisible &&
            !isOutsideMonth &&
            'outline outline-2 outline-offset-2 outline-blue-500',
          isPressed && 'scale-95',
        )}
      >
        <span className="relative">
          {formattedDate}
          {isToday && !isOutsideMonth && (
            <span className="absolute -bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-blue-600 dark:bg-blue-400" />
          )}
        </span>
      </button>
    </td>
  );
};

const SingleDatePickerCalendarGrid = ({
  state,
  startDate,
}: {
  state: ReturnType<typeof useCalendarState>;
  startDate: CalendarDate;
}) => {
  const { locale } = useLocale();
  const { gridProps, headerProps, weekDays } = useCalendarGrid(
    { startDate },
    state,
  );

  const weeksInMonth = getWeeksInMonth(startDate, locale);

  return (
    <table {...gridProps} className="w-full border-collapse border-spacing-0">
      <thead {...headerProps}>
        <tr>
          {weekDays.map((day, index) => (
            <th
              key={index}
              className="p-1 text-xs font-medium text-gray-500 dark:text-gray-400"
            >
              {day}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[...new Array(weeksInMonth).keys()].map((weekIndex) => (
          <tr key={weekIndex}>
            {state
              .getDatesInWeek(weekIndex, startDate)
              .map((date, i) =>
                date ? (
                  <SingleDatePickerCalendarCell
                    key={i}
                    state={state}
                    date={date}
                    currentMonth={startDate}
                  />
                ) : (
                  <td key={i} className="h-9 w-9 p-0" />
                ),
              )}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

function DatePicker({
  placeholder = 'Pick a date',
  className,
  dateFormat = 'PPP',
  value,
  onChange,
  disabled = false,
  autoClose = true,
  size = 'default',
  isClearable = true,
  minDate,
  maxDate,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const { locale } = useLocale();

  const selectedCalendar = value;

  const formatDate = (date: CalendarDate) => {
    const jsDate = date.toDate(getLocalTimeZone());

    // Use Intl.DateTimeFormat for locale-aware formatting
    if (dateFormat === 'PPP') {
      // PPP is like "April 29th, 2023"
      return jsDate.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }

    // For custom formats, fallback to default locale formatting
    return jsDate.toLocaleDateString(locale);
  };

  const getDisplayText = () => {
    if (!selectedCalendar) return placeholder;
    return formatDate(selectedCalendar);
  };

  const dateValue = value;

  const handleCalendarChange = (val: DateValue | null) => {
    onChange?.(val);

    if (autoClose && val) {
      setOpen(false);
    }
  };

  const handleClear = (event: React.MouseEvent<SVGSVGElement>) => {
    if (disabled) return;
    event.stopPropagation();
    onChange?.(null);
  };

  const state = useCalendarState({
    value: dateValue,
    onChange: handleCalendarChange,
    locale,
    createCalendar,
    minValue: minDate,
    maxValue: maxDate,
    isDisabled: disabled,
  });

  const ref = useRef<HTMLDivElement>(null);
  const { calendarProps, prevButtonProps, nextButtonProps } = useCalendar(
    {
      value: dateValue,
      onChange: handleCalendarChange,
      minValue: minDate,
      maxValue: maxDate,
      isDisabled: disabled,
    },
    state,
  );

  const prevButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);

  const { buttonProps: prevProps } = useButton(prevButtonProps, prevButtonRef);
  const { buttonProps: nextProps } = useButton(nextButtonProps, nextButtonRef);

  const currentMonth = state.visibleRange.start;

  const handleTodayClick = () => {
    const todayDate = today(getLocalTimeZone());
    state.setValue(todayDate);
    onChange?.(todayDate);

    if (autoClose) {
      setOpen(false);
    }
  };

  const handleClearClick = () => {
    state.setValue(null);
    onChange?.(null);
  };

  return (
    <div className={cn('grid gap-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="relative">
            <Button
              variant={'default'}
              size={size}
              className={cn(
                'w-full justify-start text-left font-normal',
                !value && 'text-muted-foreground',
              )}
              disabled={disabled}
            >
              <Icon icon="lucide:calendar" className="mr-2 h-4 w-4" />
              {getDisplayText()}
              {value && isClearable && (
                <div className="ml-auto h-4 w-4 opacity-0" />
              )}
            </Button>
            {value && isClearable && (
              <Icon
                onClick={handleClear}
                icon="lucide:x"
                className={cn(
                  'absolute right-2 top-2 h-4 w-4 cursor-pointer hover:opacity-50 dark:text-white',
                  disabled && 'cursor-default opacity-25 hover:opacity-25',
                )}
              />
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div {...calendarProps} ref={ref} className="p-3">
            <header className="relative mb-3 flex items-center justify-center">
              <Button
                {...prevProps}
                ref={prevButtonRef}
                size="small"
                iconOnly
                leftIcon="lucide:chevron-left"
                className="absolute left-0 h-7 w-7"
                disabled={prevButtonProps.isDisabled}
              />
              <Text className="text-sm font-medium" semiBold>
                {currentMonth
                  .toDate(Intl.DateTimeFormat().resolvedOptions().timeZone)
                  .toLocaleDateString(locale, {
                    month: 'long',
                    year: 'numeric',
                  })}
              </Text>
              <Button
                {...nextProps}
                ref={nextButtonRef}
                size="small"
                iconOnly
                leftIcon="lucide:chevron-right"
                className="absolute right-0 h-7 w-7"
                disabled={nextButtonProps.isDisabled}
              />
            </header>
            <div className="mb-3 flex justify-center gap-2">
              <Button
                onClick={handleTodayClick}
                size="small"
                variant="ghost"
                className="text-xs"
                disabled={disabled}
              >
                Today
              </Button>
              {value && isClearable && (
                <Button
                  onClick={handleClearClick}
                  size="small"
                  variant="ghost"
                  className="text-xs"
                  disabled={disabled}
                >
                  Clear
                </Button>
              )}
            </div>
            <SingleDatePickerCalendarGrid
              state={state}
              startDate={currentMonth}
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

DatePicker.displayName = 'DatePicker';

export { DatePicker };
